import { useTranslation } from "react-i18next"

import { Clock, ListPlus, Loader2, Send, SkipForward, X } from "@/components/icons"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { buttonVariants } from "../../../components/ui/button-variants"

export type ComposerQueueBarProps = {
  queuedMessages: { id: string; text: string }[]
  onRemove: (id: string) => void
  /** Same gating semantics as QueuedMessageBubble's onSteer/onForce — undefined
   *  means the action isn't available for the running turn, so the button for
   *  that row isn't rendered at all. */
  onSteer?: (id: string, text: string) => void
  onForce?: (id: string, text: string) => void
  /** Text of a stop-and-redirect in flight — Force, or a plain composer-draft
   *  redirect. Rendered as a "Sending…" row while the cancelled turn winds
   *  down and the redirect hasn't landed as a real message yet, so a Force
   *  never looks like it vanished the queued text — it visibly hands off
   *  from "queued" to "sending" to the tagged bubble in the feed, no gap. */
  pendingRedirectText?: string
  /** Steer notes with status `pending` — already sent to the running turn,
   *  waiting for `steer_status`. Same row layout as queued items (spinner
   *  instead of Clock) with demote-to-queue / Force / Remove. No cancel-steer
   *  WS frame exists; demote and remove are client-side only. */
  pendingSteerNotes?: { id: string; text: string }[]
  /** Move a pending steer note into `queuedMessages` (client-side). Omitted
   *  when the message queue isn't available for this session. */
  onDemotePendingSteer?: (id: string, text: string) => void
  /** Stop-and-redirect with this pending note's text (same as queued Force).
   *  Omitted when stop-redirect isn't available for the running turn. */
  onForcePendingSteer?: (id: string, text: string) => void
  /** Dismiss a pending steer note from the bar (client-side only). */
  onRemovePendingSteer?: (id: string) => void
}

/**
 * Composer queue bar (#2468 / #2469): pending queue rows, in-flight steer
 * notes, and the brief "Sending…" redirect bridge that live INSIDE the
 * composer's own bordered box, stacked directly above the textarea — never a
 * separate floating panel. Stop / generating status stays in the composer's
 * send slot; this bar does not duplicate a "Generating…" row.
 *
 * `ChatFooter` renders this as the first child of its bordered
 * `chat-dropzone` section. The bar itself owns no outer border — the parent
 * box supplies those.
 */
export function ComposerQueueBar({
  queuedMessages,
  onRemove,
  onSteer,
  onForce,
  pendingRedirectText,
  pendingSteerNotes = [],
  onDemotePendingSteer,
  onForcePendingSteer,
  onRemovePendingSteer,
}: ComposerQueueBarProps) {
  const { t } = useTranslation()

  // The bar only earns a place once there's a queued item, a steer in flight,
  // or a redirect in flight. A turn merely running is not a reason to attach
  // anything here.
  if (queuedMessages.length === 0 && pendingSteerNotes.length === 0 && !pendingRedirectText) {
    return null
  }

  const removeLabel = t("questioninput.queued_message_remove", {
    defaultValue: "Remove queued message",
  })
  const steerLabel = t("questioninput.queued_message_steer", {
    defaultValue: "Steer the running response with this instead",
  })
  const forceLabel = t("questioninput.queued_message_force", {
    defaultValue: "Stop the current response and run this next",
  })
  const demoteLabel = t("questioninput.pending_steer_queue_instead", {
    defaultValue: "Queue instead",
  })
  const removePendingLabel = t("questioninput.pending_steer_remove", {
    defaultValue: "Remove pending steer note",
  })
  const sendingLabel = t("questioninput.composer_bar_sending", {
    defaultValue: "Sending…",
  })

  const rowButtonClassName = (hoverClassName: string) =>
    cn(
      buttonVariants({ size: "icon", variant: "ghost", className: "size-7 shrink-0" }),
      "text-muted-foreground",
      hoverClassName
    )

  const hasQueuedOrPendingSteer = queuedMessages.length > 0 || pendingSteerNotes.length > 0

  return (
    <div className="bg-sidebar/40 flex flex-col">
      {queuedMessages.length > 0 && (
        <div
          className="text-muted-foreground px-3 pt-2 pb-1 text-[11px] font-semibold tracking-wide uppercase"
          data-testid="composer-queue-bar-count"
        >
          {t("questioninput.queue_bar_count", {
            defaultValue: "{{count}} Queued",
            count: queuedMessages.length,
          })}
        </div>
      )}
      {queuedMessages.map((queuedMessage) => (
        <div
          key={queuedMessage.id}
          className="border-border/60 flex items-start gap-1 border-t px-3 py-2 first:border-t-0"
          data-testid="composer-queue-bar-row"
        >
          <Clock aria-hidden="true" className="text-muted-foreground mt-1 size-3.5 shrink-0" />
          <p
            className="text-muted-foreground min-w-0 flex-1 truncate py-1 text-xs leading-snug"
            data-testid="composer-queue-bar-row-text"
          >
            {queuedMessage.text}
          </p>
          {onSteer && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label={steerLabel}
                  className={rowButtonClassName("hover:text-primary")}
                  data-testid="composer-queue-bar-steer"
                  type="button"
                  onClick={() => onSteer(queuedMessage.id, queuedMessage.text)}
                >
                  <Send className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                <p>{steerLabel}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {onForce && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label={forceLabel}
                  className={rowButtonClassName("hover:text-warning")}
                  data-testid="composer-queue-bar-force"
                  type="button"
                  onClick={() => onForce(queuedMessage.id, queuedMessage.text)}
                >
                  <SkipForward className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                <p>{forceLabel}</p>
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={removeLabel}
                className={rowButtonClassName("hover:text-destructive")}
                data-testid="composer-queue-bar-remove"
                type="button"
                onClick={() => onRemove(queuedMessage.id)}
              >
                <X className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
              <p>{removeLabel}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      ))}
      {pendingSteerNotes.map((note, index) => (
        <div
          key={note.id}
          data-testid="composer-queue-bar-steering"
          className={cn(
            "border-border/60 flex items-start gap-1 px-3 py-2",
            (queuedMessages.length > 0 || index > 0) && "border-t"
          )}
        >
          <Loader2
            aria-hidden="true"
            className="text-muted-foreground mt-1 size-3.5 shrink-0 animate-spin"
          />
          <p
            className="text-muted-foreground min-w-0 flex-1 truncate py-1 text-xs leading-snug"
            data-testid="composer-queue-bar-steering-text"
          >
            {note.text}
          </p>
          {onDemotePendingSteer && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label={demoteLabel}
                  className={rowButtonClassName("hover:text-primary")}
                  data-testid="composer-queue-bar-steering-demote"
                  type="button"
                  onClick={() => onDemotePendingSteer(note.id, note.text)}
                >
                  <ListPlus className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                <p>{demoteLabel}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {onForcePendingSteer && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label={forceLabel}
                  className={rowButtonClassName("hover:text-warning")}
                  data-testid="composer-queue-bar-steering-force"
                  type="button"
                  onClick={() => onForcePendingSteer(note.id, note.text)}
                >
                  <SkipForward className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                <p>{forceLabel}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {onRemovePendingSteer && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label={removePendingLabel}
                  className={rowButtonClassName("hover:text-destructive")}
                  data-testid="composer-queue-bar-steering-remove"
                  type="button"
                  onClick={() => onRemovePendingSteer(note.id)}
                >
                  <X className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                <p>{removePendingLabel}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      ))}
      {pendingRedirectText && (
        <div
          data-testid="composer-queue-bar-sending"
          className={cn(
            "text-muted-foreground flex items-center gap-2 px-3 py-2 text-xs",
            hasQueuedOrPendingSteer && "border-border/60 border-t"
          )}
        >
          <Loader2 aria-hidden="true" className="size-3.5 shrink-0 animate-spin" />
          <span className="flex-1 truncate">
            <span className="font-medium">{sendingLabel}</span> {pendingRedirectText}
          </span>
        </div>
      )}
    </div>
  )
}
